/**
 * How the schedule card asks for the pattern editor, without importing it. The field provides the
 * implementation; the token keeps the card testable against a stub.
 */

import { InjectionToken } from '@angular/core';
import { ScheduleRule } from '@jms/model/scheduling';

/**
 * The two TASK-level dates the editor edits alongside the schedule.
 *
 * Neither belongs to the schedule object — `startsOn` is the task's own start override and
 * `lastCompleted` is when it was last serviced — but both answer questions the editor asks ("when is
 * the first one due?", "when was it last done?"), so the dialog owns them and hands them back here.
 */
export interface ScheduleEditorDates {
  /** Undefined means the task starts with its Service Line — the caller clears any stored override. */
  startsOn?: string;
  /** Undefined means nothing recorded — the caller clears any stored value. */
  lastCompleted?: string;
}

export interface ScheduleEditorResult extends ScheduleEditorDates {
  schedule: ScheduleRule;
}

export interface ScheduleEditorLauncher {
  /**
   * Resolves the edited schedule and task dates on save, or undefined when the user cancels.
   *
   * `dates` seeds the editor with what the task currently stores; every key of the resolved result is
   * authoritative, so an absent `startsOn` on the way out means "clear it", not "leave it alone".
   */
  open(model: ScheduleRule | undefined, options: { readonly?: boolean; dates?: ScheduleEditorDates }): Promise<ScheduleEditorResult | undefined>;
}

export const SCHEDULE_EDITOR_LAUNCHER = new InjectionToken<ScheduleEditorLauncher>('SCHEDULE_EDITOR_LAUNCHER');
